import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import Logo from '../components/Logo'
import MovieCard from '../components/MovieCard'
import Page from '../components/Page'
import TabBar from '../components/TabBar'

export default function Watchlist() {
  const { user } = useAuth()
  const { activeGroup } = useGroup()
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeSession, setActiveSession] = useState(null)
  const [mySubmission, setMySubmission] = useState(null)
  const [genreFilter, setGenreFilter] = useState('All')
  const [submitting, setSubmitting] = useState(null)

  useEffect(() => {
    loadData()
  }, [user, activeGroup])

  async function loadData() {
    if (!user) return
    setLoading(true)

    if (activeGroup) {
      // Active session (open or mid-spin) so we can offer "submit" actions
      const { data: session } = await supabase
        .from('movie_night_sessions')
        .select('*')
        .eq('group_id', activeGroup.id)
        .in('status', ['open', 'spinning'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      setActiveSession(session || null)

      if (session) {
        const { data: sub } = await supabase
          .from('session_submissions')
          .select('*, movies(*)')
          .eq('session_id', session.id)
          .eq('user_id', user.id)
          .maybeSingle()
        setMySubmission(sub || null)
      } else {
        setMySubmission(null)
      }
    }

    const { data: list } = await supabase
      .from('watchlist_items')
      .select('*, movies(*)')
      .eq('user_id', user.id)
      .order('added_at', { ascending: false })

    setItems(list || [])
    setLoading(false)
  }

  async function removeItem(id) {
    await supabase.from('watchlist_items').delete().eq('id', id)
    loadData()
  }

  async function submitToSession(movieId) {
    if (!activeSession) return
    setSubmitting(movieId)
    try {
      if (mySubmission) {
        // Swap existing submission to new movie
        await supabase
          .from('session_submissions')
          .update({ movie_id: movieId })
          .eq('id', mySubmission.id)
      } else {
        await supabase.from('session_submissions').insert({
          session_id: activeSession.id,
          user_id: user.id,
          movie_id: movieId,
        })
      }
      await loadData()
    } catch (err) {
      console.error(err)
      alert('Could not submit pick. Try again.')
    } finally {
      setSubmitting(null)
    }
  }

  // Build genre set from all items
  const allGenres = new Set()
  items.forEach(i => (i.movies?.genres || []).forEach(g => g && allGenres.add(g)))
  const genreOptions = ['All', ...Array.from(allGenres).sort()]

  const filtered = genreFilter === 'All'
    ? items
    : items.filter(i => (i.movies?.genres || []).includes(genreFilter))

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <span className="text-[var(--fp-text-muted)]">Loading…</span>
      </div>
    )
  }

  return (
    <Page mode="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between py-4 mb-10">
        <Logo size="md" />
        <button
          onClick={() => navigate('/add')}
          className="font-semibold text-white border-none cursor-pointer rounded-full"
          style={{
            background: 'var(--fp-red)',
            padding: '12px 24px',
            fontSize: '15px',
            boxShadow: '0 4px 20px var(--fp-red-glow)',
          }}
        >
          + Add movie
        </button>
      </div>

      <h1 className="fp-display text-3xl text-white mb-12">My Watchlist</h1>

      {activeSession && (
        <div className="fp-card-elevated mb-12">
          <div className="text-[11px] fp-gold-text uppercase tracking-[0.18em] font-semibold">
            Movie night is on
          </div>
          <div className="text-sm text-white mt-1.5">
            {mySubmission
              ? <>You're in with <span className="font-semibold">{mySubmission.movies?.title}</span>. Swap it anytime before the spin.</>
              : <>Pick one from your list to lock in your submission.</>}
          </div>
          {mySubmission && (
            <button
              onClick={() => navigate('/')}
              className="fp-btn fp-btn-primary mt-3"
            >
              Back to {activeGroup?.name || 'the crew'} →
            </button>
          )}
        </div>
      )}

      {genreOptions.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-4 mb-8 -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
          {genreOptions.map(g => (
            <button
              key={g}
              onClick={() => setGenreFilter(g)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border cursor-pointer"
              style={{
                background: genreFilter === g ? 'var(--fp-red)' : 'var(--fp-surface)',
                borderColor: genreFilter === g ? 'var(--fp-red)' : 'var(--fp-border)',
                color: '#fff',
              }}
            >
              {g}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="fp-card text-center py-10 mb-4">
          <div className="text-4xl mb-2">🎬</div>
          <p className="text-[var(--fp-text-muted)] text-sm">
            {items.length === 0
              ? "Your watchlist is empty. Add some movies so you're ready for movie night."
              : `Nothing in "${genreFilter}" yet.`}
          </p>
          {items.length === 0 && (
            <button
              onClick={() => navigate('/add')}
              className="fp-btn fp-btn-primary max-w-xs mx-auto mt-4"
            >
              Add your first movie
            </button>
          )}
        </div>
      ) : (
        filtered.map(item => {
          const isMyPick = mySubmission?.movie_id === item.movie_id
          return (
            <MovieCard
              key={item.id}
              movie={item.movies}
              onClick={() => navigate(`/movie/${item.movies?.tmdb_id}`)}
              actions={
                <div className="flex gap-3 items-center flex-wrap">
                  {activeSession && (
                    isMyPick ? (
                      <span className="text-xs font-semibold" style={{ color: 'var(--fp-red)' }}>
                        ★ Your pick tonight
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); submitToSession(item.movie_id) }}
                        disabled={submitting === item.movie_id}
                        className="text-xs font-semibold bg-transparent border-none cursor-pointer disabled:opacity-50"
                        style={{ color: 'var(--fp-red)' }}
                      >
                        {submitting === item.movie_id ? 'Submitting…' : mySubmission ? 'Swap to this' : 'Submit for tonight'}
                      </button>
                    )
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeItem(item.id) }}
                    className="text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer hover:text-[var(--fp-red)]"
                  >
                    Remove
                  </button>
                </div>
              }
            />
          )
        })
      )}

      <TabBar />
    </Page>
  )
}
